import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { IsoDateSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * ACCOUNTING PERIODS (Q-FIN-01-3 — "nothing models a closed accounting
 * period, although other screens must refuse to post into one. Proposed
 * default: add a period table with a status, and make posting check it
 * everywhere").
 *
 * ── What a period is ────────────────────────────────────────────────────────
 *
 * One CALENDAR MONTH of the tenant's own financial cycle. The cycle itself is
 * two settings that already exist — `fiscal.cycle_start` and
 * `fiscal.cycle_end` (§15.6.7: "Start of the cycle 2026-01-01 / End of the
 * cycle 2026-12-31") — so the calendar is DERIVED, never typed: every month
 * the cycle touches is a period, named `YYYY-MM`, and that name is the one
 * that appears in the refusal sentence (GEN-92's "2026-06 is closed").
 *
 * A period is open or closed. Absence of a row means OPEN: a tenant that has
 * never closed anything is not blocked by anything, and the guard is a single
 * EXISTS over the closed rows.
 *
 * ── The two rules it enforces ───────────────────────────────────────────────
 *
 *   GEN-92  "A document must not be confirmed with a date inside a closed
 *           period. The message must be: '2026-06 is closed. Pick a date in
 *           an open period, or ask your accountant to reopen it.'"
 *   GEN-95  "A document dated in the future must be allowed while Draft, and
 *           must be refused on confirmation, with: 'You cannot confirm a
 *           document dated after today.'"
 *
 * Both are settings-gated by the two OWNER-ONLY switches of §15.6.7, which
 * shipped in the registry long before the behaviour did:
 * `security.lock_closed_periods` (default on, settles GEN-92) and
 * `security.block_future_dated` (default on, settles GEN-95). GEN-200 is why
 * they are owner-scope: "An owner setting must be the only one that can
 * weaken a financial control. Locking periods and blocking future dates are
 * owner settings for that reason."
 *
 * Neither rule is a permission, and the Owner flag does NOT short-circuit
 * them — BR-PERM-10, BR-PERM-11 and §16.4's "the short-circuit answers
 * permission questions, not business rules" all say so explicitly.
 *
 * ── Closing and reopening ───────────────────────────────────────────────────
 *
 *   GEN-93  "Closing a period must be refused while any document dated inside
 *           it is still Draft. The system must list those documents."
 *   GEN-94  "Reopening a closed period must be allowed only to a user whose
 *           role grants it, and must write one Audit Log row."
 *
 * Permission area is `accounting` (16.2.2 row 6: "Chart of accounts,
 * currencies, taxes, fiscal periods"). Closing is the area's Approve action,
 * exactly as 16.5's own example assigns posting — "`accounting.approve` for
 * posting a journal"; closing the month is the same class of act and 16.3's
 * "Approve covers both approving and reversing" is the shape.
 *
 * Reopening is NOT the same grant, because two spec statements have to hold
 * at once: GEN-94 wants it role-grantable, and EC-19-21 wants it "restricted
 * to the account carrying the Owner flag". Both are satisfied by the pattern
 * this codebase already has for `banking.override_bounced_flag` (owner ruling
 * R10) — a NAMED EXTENSION to the 12x5 grid, `accounting.reopen_period`, held
 * by NO shipped role. Out of the box only the Owner can reopen (EC-19-21); an
 * Owner who wants an accountant to do it grants the row deliberately (GEN-94),
 * and the grant is visible on the Roles screen like every other (BR-PERM-37).
 */
// ── The fixed sentences ─────────────────────────────────────────────────────
export const PERIOD_MESSAGES = {
    /**
     * GEN-92, verbatim, with the period named. Also the row in §4.16's
     * message table ("2026-06 is closed. Pick a date in an open period." — the
     * shorter form; GEN-92's is the fuller one and adds who may reopen, which
     * is what a person actually needs).
     *
     * ONE sentence for every screen that can trip it. Five of the spec's error
     * tables quote their own rendering of it — ERR-SAL-03-05, ERR-PUR-02-05,
     * ERR-MFG-02-11, ERR-MFG-04-06 — and they differ from each other in the
     * verb ("Use a date" / "Pick a date") and in how the period is written
     * ("June 2026" / "July 2026"). GEN-92 is the one that governs, because a
     * closed period refuses the same way whoever walked into it; five screen
     * copies would be five chances to drift.
     */
    closed: (code) => `${code} is closed. Pick a date in an open period, or ask your accountant to reopen it.`,
    /** GEN-95, verbatim. */
    futureDated: 'You cannot confirm a document dated after today.',
    /** GEN-93 — the count leads, the list follows on the screen. */
    hasDrafts: (code, drafts) => `${code} still holds ${drafts} draft ${drafts === 1 ? 'document' : 'documents'}. Post or delete them before closing the period.`,
    alreadyClosed: (code) => `${code} is already closed.`,
    alreadyOpen: (code) => `${code} is already open.`,
    /** A month outside `fiscal.cycle_start`..`fiscal.cycle_end` is not a period. */
    notInCycle: (code) => `${code} is outside the financial cycle. Change the cycle dates in General Settings first.`,
    /** EC-19-21's own sentence, for the reopen refusal (the permission answer). */
    reopenForbidden: 'This period is closed. Ask the owner to reopen it.',
    /** The confirmation the screen types out before reopening (GEN-94). */
    reopenConsequence: (code) => `Reopening ${code} lets documents be posted into a month that has already been reported. The change is written to the Audit Log.`,
};
/**
 * D46 — the family's CONVERTED parameterised refusals, structured. Each
 * builder composes the same sentence as its `PERIOD_MESSAGES` twin (asserted
 * by `refusal-census.spec.ts`, which also holds the roster of siblings still
 * recorded-unconverted) and names the ICU key the web dictionaries answer.
 */
export const PERIOD_REFUSALS = {
    /** GEN-92 — raised by `PeriodGuardFilter` from every posting door, and by
     *  the stock writer's own date gate. */
    closed: (code) => ({
        message: PERIOD_MESSAGES.closed(code),
        key: 'periods.errClosed',
        params: { code },
    }),
    /** GEN — the period is already closed; raised by the close door. */
    alreadyClosed: (code) => ({
        message: PERIOD_MESSAGES.alreadyClosed(code),
        key: 'periods.errAlreadyClosed',
        params: { code },
    }),
    /** The period is already open; raised by the reopen door (both the
     *  never-closed and the already-reopened arms compose this one sentence). */
    alreadyOpen: (code) => ({
        message: PERIOD_MESSAGES.alreadyOpen(code),
        key: 'periods.errAlreadyOpen',
        params: { code },
    }),
    /** GEN-93 — the draft count leads; the blocker list rides beside this body
     *  on the close refusal. `drafts` is a genuine count the dictionaries
     *  pluralise (noun-only: document / documents). */
    hasDrafts: (code, drafts) => ({
        message: PERIOD_MESSAGES.hasDrafts(code, drafts),
        key: 'periods.errHasDrafts',
        params: { code, drafts },
    }),
    /** A month outside the fiscal cycle is not a period; raised by the close and
     *  reopen doors' shared cycle gate. */
    notInCycle: (code) => ({
        message: PERIOD_MESSAGES.notInCycle(code),
        key: 'periods.errNotInCycle',
        params: { code },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
/** `YYYY-MM` — the period's name, and the one that appears in GEN-92. */
export const PeriodCodeSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
export const AccountingPeriodSchema = z
    .object({
    /** `YYYY-MM`. Stable, human, and the path param. */
    code: PeriodCodeSchema,
    startsOn: IsoDateSchema,
    endsOn: IsoDateSchema,
    status: z.enum(['open', 'closed']),
    /** Who closed it and when — null while open (GEN-94's audit trail, on the row). */
    closedAt: z.string().nullable(),
    closedByName: z.string().nullable(),
    /** The LAST reopening, if any. A reopened period keeps its history here. */
    reopenedAt: z.string().nullable(),
    reopenedByName: z.string().nullable(),
    reopenReason: z.string().nullable(),
    /** Posted journal entries dated inside it — what closing seals. */
    postedEntries: z.number().int(),
    /** Draft JOURNAL entries dated inside it — the column the screen shows.
     *  GEN-93's full blocker list spans every document type and travels on
     *  the close refusal, where it is actually needed. */
    draftEntries: z.number().int(),
    /** The period containing the company's own today (N-16), for the screen. */
    isCurrent: z.boolean(),
})
    .strict();
export const PeriodListResponseSchema = z
    .object({
    periods: AccountingPeriodSchema.array(),
    /** `fiscal.cycle_start` / `fiscal.cycle_end` — the calendar's source. */
    cycleStart: IsoDateSchema,
    cycleEnd: IsoDateSchema,
    /** The COMPANY's today, from its own `fiscal.timezone` (N-16). */
    today: IsoDateSchema,
    /** The two owner switches, so the screen can say the guard is off. */
    lockClosedPeriods: z.boolean(),
    blockFutureDated: z.boolean(),
    /** `accounting.approve`, and the Owner-or-`accounting.reopen_period` pair. */
    canClose: z.boolean(),
    canReopen: z.boolean(),
})
    .strict();
/** GEN-93's list — what the screen shows instead of a bare refusal. */
export const PeriodBlockerSchema = z
    .object({
    kind: z.string(),
    reference: z.string(),
    date: IsoDateSchema,
})
    .strict();
export const PeriodCloseRefusalSchema = z
    .object({ message: z.string(), blockers: PeriodBlockerSchema.array() })
    .strict();
const periodParams = z.object({ code: PeriodCodeSchema }).strict();
// ── Routes ──────────────────────────────────────────────────────────────────
export const accountingPeriodsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/accounting/periods',
        responses: {
            200: PeriodListResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The fiscal calendar derived from fiscal.cycle_start/cycle_end, each month open or closed (accounting.view).',
    },
    close: {
        method: 'POST',
        path: '/api/accounting/periods/:code/close',
        pathParams: periodParams,
        body: c.noBody(),
        responses: {
            200: AccountingPeriodSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            /** GEN-93 — refused while drafts remain, WITH the list. */
            409: PeriodCloseRefusalSchema,
        },
        summary: 'Close a month (accounting.approve). Refused while it holds drafts (GEN-93).',
    },
    reopen: {
        method: 'POST',
        path: '/api/accounting/periods/:code/reopen',
        pathParams: periodParams,
        body: z.object({ reason: z.string().trim().min(1).max(500) }).strict(),
        responses: {
            200: AccountingPeriodSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Reopen a closed month — the Owner, or a role holding accounting.reopen_period (GEN-94, EC-19-21). Reason required; audited separately from closing.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=accounting-periods.js.map